import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Building2, 
  Plus, 
  ArrowLeft,
  Users,
  Globe,
  Factory
} from 'lucide-react';
import NavBar from '../components/NavBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompaniesPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [companies, setCompanies] = useState([]);
  const [industries, setIndustries] = useState({ tr: [], en: [] });
  const [countries, setCountries] = useState({ tr: [], en: [] });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    size: 'mid_market',
    country: '',
    employee_count: '',
    annual_revenue: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [companiesRes, industriesRes, countriesRes] = await Promise.all([
        axios.get(`${API}/companies`),
        axios.get(`${API}/meta/industries`),
        axios.get(`${API}/meta/countries`)
      ]);
      setCompanies(companiesRes.data);
      setIndustries(industriesRes.data);
      setCountries(countriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const payload = {
        ...formData,
        employee_count: parseInt(formData.employee_count) || 0,
        annual_revenue: formData.annual_revenue ? parseFloat(formData.annual_revenue) : null
      };
      
      await axios.post(`${API}/companies`, payload);
      setDialogOpen(false);
      setFormData({
        name: '',
        industry: '',
        size: 'mid_market',
        country: '',
        employee_count: '',
        annual_revenue: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error creating company:', error);
    } finally {
      setSubmitting(false);
    }
  };
  
  const getSizeLabel = (size) => {
    const labels = {
      sme: { tr: 'KOBİ', en: 'SME' },
      mid_market: { tr: 'Orta Ölçekli', en: 'Mid-Market' },
      enterprise: { tr: 'Kurumsal', en: 'Enterprise' }
    };
    return labels[size]?.[language] || size;
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <NavBar />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-white">{t('loading')}</div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-950" data-testid="companies-page">
      <NavBar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">{t('companies')}</h1>
            <p className="text-slate-400">
              {language === 'tr' 
                ? 'Değerlendirme yapacağınız şirketleri yönetin'
                : 'Manage companies for assessments'}
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-company-btn">
                <Plus className="mr-2 h-4 w-4" />
                {t('addCompany')}
              </Button>
            </DialogTrigger>
            
            <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle>{t('addCompany')}</DialogTitle>
                <DialogDescription className="text-slate-400">
                  {language === 'tr' 
                    ? 'Yeni şirket bilgilerini girin'
                    : 'Enter new company information'}
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('companyName')}</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder={language === 'tr' ? 'Şirket adı' : 'Company name'}
                    required
                    data-testid="company-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('industry')}</Label>
                  <Select
                    value={formData.industry}
                    onValueChange={(value) => setFormData({ ...formData, industry: value })}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="industry-select">
                      <SelectValue placeholder={language === 'tr' ? 'Sektör seçin' : 'Select industry'} />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {industries[language]?.map((ind, i) => (
                        <SelectItem key={i} value={ind} className="text-white hover:bg-slate-700">
                          {ind}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('companySize')}</Label>
                  <Select
                    value={formData.size}
                    onValueChange={(value) => setFormData({ ...formData, size: value })}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="size-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="sme" className="text-white hover:bg-slate-700">
                        {t('sme')} (1-250)
                      </SelectItem>
                      <SelectItem value="mid_market" className="text-white hover:bg-slate-700">
                        {t('midMarket')} (251-1000)
                      </SelectItem>
                      <SelectItem value="enterprise" className="text-white hover:bg-slate-700">
                        {t('enterprise')} (1000+)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('country')}</Label>
                  <Select
                    value={formData.country}
                    onValueChange={(value) => setFormData({ ...formData, country: value })}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="country-select">
                      <SelectValue placeholder={language === 'tr' ? 'Ülke seçin' : 'Select country'} />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                      {countries[language]?.map((country, i) => (
                        <SelectItem key={i} value={country} className="text-white hover:bg-slate-700">
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('employeeCount')}</Label>
                  <Input
                    type="number"
                    value={formData.employee_count}
                    onChange={(e) => setFormData({ ...formData, employee_count: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="500"
                    required
                    data-testid="employee-count-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('annualRevenue')} (USD)</Label>
                  <Input
                    type="number"
                    value={formData.annual_revenue}
                    onChange={(e) => setFormData({ ...formData, annual_revenue: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="10000000"
                    data-testid="revenue-input"
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={submitting}
                    data-testid="save-company-btn"
                  >
                    {submitting ? t('loading') : t('save')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        
        {/* Companies Grid */}
        {companies.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {companies.map((company) => (
              <Card 
                key={company.id} 
                className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-colors cursor-pointer"
                data-testid={`company-card-${company.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 rounded-lg bg-blue-500/10 text-blue-400">
                      <Building2 size={24} />
                    </div>
                    <span className="px-2 py-1 text-xs rounded-full bg-slate-800 text-slate-400">
                      {getSizeLabel(company.size)}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-white mb-2">{company.name}</h3>
                  
                  <div className="space-y-2 text-sm text-slate-400">
                    <div className="flex items-center space-x-2">
                      <Factory size={14} />
                      <span>{company.industry}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Globe size={14} />
                      <span>{company.country}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Users size={14} />
                      <span>{company.employee_count?.toLocaleString()} {language === 'tr' ? 'çalışan' : 'employees'}</span>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-slate-800">
                    <Link to={`/assessments/new?company=${company.id}`}>
                      <Button 
                        size="sm" 
                        className="w-full bg-slate-800 hover:bg-slate-700 text-white"
                        data-testid={`start-assessment-${company.id}`}
                      >
                        {t('startNewAssessment')}
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="py-16 text-center">
              <Building2 className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">
                {language === 'tr' ? 'Henüz şirket eklenmemiş' : 'No companies added yet'}
              </h3>
              <p className="text-slate-400 mb-6">
                {language === 'tr' 
                  ? 'Değerlendirme yapmak için önce bir şirket ekleyin'
                  : 'Add a company to start assessments'}
              </p>
              <Button 
                onClick={() => setDialogOpen(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('addCompany')}
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default CompaniesPage;
